const DEPLOYED_API_ROOT = "https://saas-back-t7cj.onrender.com";

function getApiRoot() {
  const configured = process.env.REACT_APP_API_BASE_URL || "";
  if (configured) {
    return configured
      .replace(/\/$/, "")
      .replace(/\/api\/v1$/i, "")
      .replace(/\/api\/enrollme$/i, "")
      .replace(/\/api$/i, "");
  }
  if (typeof window !== "undefined" && window.location.hostname === "enrollme-s1e1.onrender.com") {
    return DEPLOYED_API_ROOT;
  }
  return "";
}

const API_ROOT = getApiRoot();
const BASE = `${API_ROOT}/api/enrollme/admin`;

async function reqBlob(path) {
  const res = await fetch(`${BASE}${path}`, { method: "GET", credentials: "include" });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.message || "Request failed"), { status: res.status });
  }
  return res.blob();
}

async function reqBlobPost(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw Object.assign(new Error(data.message || "Request failed"), { status: res.status });
  }
  return res.blob();
}

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function req(method, path, body, isFormData = false) {
  const opts = { method, credentials: "include" };
  if (body) {
    if (isFormData) {
      opts.body = body;
    } else {
      opts.headers = { "Content-Type": "application/json" };
      opts.body = JSON.stringify(body);
    }
  }
  const res = await fetch(`${BASE}${path}`, opts);
  const contentType = res.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await res.json().catch(() => ({}))
    : {};
  if (!contentType.includes("application/json")) {
    throw Object.assign(new Error("API did not return JSON. Check REACT_APP_API_BASE_URL for the EnrollMe deployment."), { status: res.status, data });
  }
  if (!res.ok) throw Object.assign(new Error(data.message || "Request failed"), { status: res.status, data });
  return data;
}

export function getPublicSettings() {
  return fetch(`${API_ROOT}/api/enrollme/public/settings`)
    .then((r) => r.json())
    .catch(() => ({ companyName: "", companyTradeName: "" }));
}

export const adminApi = {
  login: (email, password) => req("POST", "/login", { email, password }),
  logout: () => req("POST", "/logout"),
  me: () => req("GET", "/me"),

  // Drivers
  listDrivers: (params = {}) => req("GET", `/drivers?${new URLSearchParams(params)}`),
  createDriver: (data) => req("POST", "/drivers", data),
  getDriver: (id) => req("GET", `/drivers/${id}`),
  updateDriver: (id, data) => req("PATCH", `/drivers/${id}`, data),
  resendDriverWelcomeEmail: (id) => req("POST", `/drivers/${id}/resend-welcome-email`),
  deleteDriver: (id) => req("DELETE", `/drivers/${id}`),

  // Insurance card
  uploadInsuranceCard: (driverId, formData) => req("POST", `/drivers/${driverId}/insurance-card`, formData, true),
  getInsuranceCardLink: (driverId) => req("GET", `/drivers/${driverId}/insurance-card`),
  deleteInsuranceCard: (driverId) => req("DELETE", `/drivers/${driverId}/insurance-card`),

  // Signed documents
  getSignedDocLink: (driverId, documentId) => req("GET", `/drivers/${driverId}/signed-documents/${documentId}/download`),

  // Compliance docs
  listDocs: (ownerType, ownerId) => req("GET", `/${ownerType}/${ownerId}/documents`),
  uploadDoc: (ownerType, ownerId, formData) => req("POST", `/${ownerType}/${ownerId}/documents`, formData, true),
  getDocLink: (docId) => req("GET", `/documents/${docId}/link`),
  deleteDoc: (docId) => req("DELETE", `/documents/${docId}`),

  // Vehicles
  listVehicles: (params = {}) => req("GET", `/vehicles?${new URLSearchParams(params)}`),
  createVehicle: (data) => req("POST", "/vehicles", data),
  getVehicle: (id) => req("GET", `/vehicles/${id}`),
  updateVehicle: (id, data) => req("PATCH", `/vehicles/${id}`, data),
  deleteVehicle: (id) => req("DELETE", `/vehicles/${id}`),

  // Reports
  getDriverVehicleReport: async () => {
    const [drivers, vehicles] = await Promise.all([
      req("GET", "/drivers"),
      req("GET", "/vehicles"),
    ]);
    return {
      drivers: drivers.drivers || [],
      vehicles: vehicles.vehicles || [],
    };
  },

  // Compliance overview
  getCompliance: () => req("GET", "/compliance"),
  triggerNotify: () => req("POST", "/compliance/notify"),
  getNotificationLogs: () => req("GET", "/compliance/logs"),

  // Messages
  listMessages: () => req("GET", "/messages"),
  sendMessage: (data) => req("POST", "/messages", data),
  getMessageThread: (id) => req("GET", `/messages/${id}`),

  // Settings
  getSettings: () => req("GET", "/settings"),
  updateSettings: (data) => req("PATCH", "/settings", data),

  // Verification letter
  downloadVerificationLetter: async (driverId) => {
    const blob = await reqBlob(`/drivers/${driverId}/verification-letter`);
    triggerBlobDownload(blob, "Independent_Contractor_Verification.pdf");
  },

  // AVI letter
  getAviPrefill: (driverId) => req("GET", `/drivers/${driverId}/avi-prefill`),
  getVehicleByCab: (cabNumber) => req("GET", `/vehicles/by-cab/${encodeURIComponent(cabNumber)}`),
  generateAviLetter: async (data) => {
    const blob = await reqBlobPost("/avi-letter", data);
    const typeLabel = { request_tag: "AVI_Tag_Request", swap: "AVI_Swap", refund: "AVI_Refund" }[data.type] || "AVI_Letter";
    triggerBlobDownload(blob, `${typeLabel}_Cab${data.cabNumber || "X"}.pdf`);
  },
};
